import anime from "animejs/lib/anime.es.js";
import React from "react";
import { Spinner } from "component-library";
import { ChevronRightIcon, ChevronLeftIcon, OpenInNewTabIcon } from "assets";

import { Photo } from "models";

import {
  FULL_GALLERY_WIDTH_LIST,
  getImageSourcesSettings,
  getImageUrlWithCustomWidth,
  getOriginalImageUrl,
  ImageWidth,
} from "services/UrlBuilder";

import { MediaCloseButton } from "components/common/MediaCloseButton";
import { Picture } from "components/common/Picture";

import "./FullScreenGallery.scss";

const MINIMAL_TOUCH_MOVE = 10;
const MINIMAL_SLIDE_MOVE = 150;
const ANIMATION_DURATION = 300;
const ACTION_DURATION = 500;

enum Vector {
  left = -1,
  right = 1,
}

export interface IFullScreenGalleryContent {
  connector: string;
  openFullPhoto: string;
}

export interface IFullScreenGalleryProps {
  photo: Photo;
  galleryPhotos: Photo[];
  content: IFullScreenGalleryContent;
  photoDataRequests: Map<string, Promise<Photo | undefined>>;
  closePhoto: () => void;
}

interface IFullScreenGalleryState {
  currentIndex: number;
  signature: string;
  clicksOutside: number;
}

export class FullScreenGallery extends React.Component<
  IFullScreenGalleryProps,
  IFullScreenGalleryState
> {
  public readonly state: Readonly<IFullScreenGalleryState> = {
    currentIndex: 0,
    signature: "",
    clicksOutside: 0,
  };

  private shadowBoxRef = React.createRef<HTMLDivElement>();
  private imageRef = React.createRef<HTMLImageElement>();
  private galleryContainerRef = React.createRef<HTMLDivElement>();
  private photoBoxRef = React.createRef<HTMLDivElement>();
  private placeholderRef = React.createRef<HTMLDivElement>();
  private spinnerRef = React.createRef<HTMLDivElement>();

  private loadedSignatures = new Map<string, string>();
  private xDown: number;
  private xLastDown: number;
  private prevDirection: number;

  public render() {
    const { galleryPhotos, closePhoto, content } = this.props;
    const { currentIndex, signature } = this.state;
    const currentPhoto = galleryPhotos[currentIndex];

    return (
      <div ref={this.galleryContainerRef} className="c-full-screen-gallery">
        <div
          className="c-full-screen-gallery__container"
          ref={this.photoBoxRef}
        >
          <div className="c-full-screen-gallery__close control">
            <MediaCloseButton closeAction={closePhoto} />
          </div>
          {this.renderImage(currentPhoto)}
          {this.renderImagePlaceHolder()}
          {this.renderButtons()}
          <div
            ref={this.shadowBoxRef}
            className="c-full-screen-gallery__shadow-box"
          />
          {this.renderSignature(signature)}
          {this.renderImageActionButtons(currentPhoto.url, content)}
        </div>
      </div>
    );
  }

  private renderImagePlaceHolder = () => (
    <div
      ref={this.placeholderRef}
      className="c-full-screen-gallery__placeholder"
    >
      <div ref={this.spinnerRef} className="c-full-screen-gallery__spinner">
        <Spinner />
      </div>
    </div>
  );

  private renderButtons = () => (
    <React.Fragment>
      <div
        className="c-full-screen-gallery__next control"
        onClick={this.showNextImage}
      >
        <ChevronRightIcon />
      </div>
      <div
        className="c-full-screen-gallery__prev control"
        onClick={this.showPrevImage}
      >
        <ChevronLeftIcon />
      </div>
    </React.Fragment>
  );

  private renderSignature = (signature: string) => (
    <div className="c-full-screen-gallery__info control">{signature}</div>
  );

  public componentDidMount() {
    document.addEventListener("click", this.outsideClick);
    document.addEventListener("keydown", this.movePhotoByKeyboard, false);

    const { current: image } = this.imageRef;

    if (image) {
      image.addEventListener("touchstart", this.startMoving, false);
      image.addEventListener("dragstart", this.startMoving, false);
      image.addEventListener("dragover", this.handleMove, false);
      image.addEventListener("touchmove", this.handleMove, false);
      image.addEventListener("touchend", this.handleMoveEnd, false);
      image.addEventListener("dragend", this.handleMoveEnd, false);
      image.addEventListener("load", this.loadImageHandler, false);
    }

    const { photo, galleryPhotos } = this.props;
    const photoIndex = galleryPhotos.findIndex(
      (galleryPhoto) => galleryPhoto.id === photo.id
    );
    this.updatePhotoSignatureText(photoIndex);

    this.setState({ currentIndex: photoIndex });
  }

  public componentWillUnmount() {
    document.removeEventListener("click", this.outsideClick);
    document.removeEventListener("keydown", this.movePhotoByKeyboard);
  }

  private renderImage = ({ url }: Photo) => {
    const imageUrl = getImageUrlWithCustomWidth(url, ImageWidth.WidthLarge);
    const sourcesSettings = getImageSourcesSettings(
      url,
      FULL_GALLERY_WIDTH_LIST
    );

    return (
      <Picture
        sourcesSettings={sourcesSettings}
        imgRef={this.imageRef}
        className="c-full-screen-gallery__photo"
        onError={this.errorImageHandler}
        src={imageUrl}
      />
    );
  };

  private renderImageActionButtons = (
    imageId: string,
    content: IFullScreenGalleryContent
  ) => {
    const imgUrl = getOriginalImageUrl(imageId);

    return (
      <a
        className="c-full-screen-gallery__original-icon control"
        href={imgUrl}
        target="_blank"
        title={content.openFullPhoto}
        tabIndex={0}
        rel="noreferrer"
      >
        <OpenInNewTabIcon />
      </a>
    );
  };

  private movePhotoByKeyboard = (event: KeyboardEvent) => {
    const { key } = event;

    if (key === "Left" || key === "ArrowLeft") {
      event.stopPropagation();
      event.preventDefault();
      this.showPrevImage();
    }

    if (key === "Right" || key === "ArrowRight") {
      event.stopPropagation();
      event.preventDefault();
      this.showNextImage();
    }
  };

  private hideImage = () => {
    const { current: image } = this.imageRef;

    if (image) {
      image.style.display = "none";
    }
  };

  private outsideClick = (event: MouseEvent) => {
    if (
      this.state.clicksOutside > 0 &&
      !this.photoBoxRef.current?.contains(event.target as Node)
    ) {
      this.props.closePhoto();
    }

    this.setState((current) => ({
      clicksOutside: current.clicksOutside + 1,
    }));
  };

  private errorImageHandler = () => {
    this.switchToNextImage(this.prevDirection);
    this.resetPlaceholderStyles();
  };

  private startMoving = (event: TouchEvent & DragEvent) => {
    if (event.touches) {
      const { clientX } = event.touches[0];
      this.xDown = clientX;
    } else {
      this.xDown = event.clientX;
    }

    this.xLastDown = this.xDown;

    this.hideControls();
  };

  private handleMove = (event: TouchEvent & DragEvent) => {
    event.preventDefault();

    if (this.xDown) {
      let currentX = 0;

      if (event.touches) {
        const { clientX } = event.touches[0];
        currentX = clientX;
      } else {
        currentX = event.clientX;
      }

      if (Math.abs(this.xLastDown - currentX) > MINIMAL_TOUCH_MOVE) {
        this.xLastDown = currentX;
        const xDiff = currentX - this.xDown;

        if (Math.abs(xDiff) > MINIMAL_TOUCH_MOVE) {
          this.createShadowBox();
          this.animateImageMovingTo(xDiff);
        }
      }
    }
  };

  private handleMoveEnd = () => {
    if (this.xDown) {
      const xDiff = this.xLastDown - this.xDown;

      const actions = new Array<VoidFunction>();

      if (Math.abs(xDiff) > MINIMAL_SLIDE_MOVE) {
        this.moveInDirection(Math.sign(xDiff));
        actions.push(() => this.switchToNextImage(-Math.sign(xDiff)));
        actions.push(this.resetPlaceholderStyles);
        this.delayAnimation(this.hideImage);
      } else {
        this.animateImageMovingTo(0);
      }

      actions.push(this.unmountShadowBox);
      actions.push(this.showControls);

      this.delayAction(...actions);
      this.xDown = 0;
      this.xLastDown = 0;
    }
  };

  private showNextImage = () => this.triggerMoving(Vector.right);

  private showPrevImage = () => this.triggerMoving(Vector.left);

  private triggerMoving = (indexImageMoving: Vector) => {
    const switchToNextImage = () => this.switchToNextImage(indexImageMoving);
    switchToNextImage();
  };

  private moveInDirection = (vectorModifier: Vector) => {
    const { current: image } = this.imageRef;

    if (image) {
      const { width: imgWidth } = image;

      this.animateImageMovingTo(vectorModifier * (imgWidth + 1));
    }
  };

  private switchToNextImage = (vectorModifier: Vector) => {
    const { current: image } = this.imageRef;

    if (image) {
      image.src = "";
      image.style.transform = "translateX(0)";

      const { galleryPhotos } = this.props;
      const { currentIndex } = this.state;

      let nextIndex = currentIndex + vectorModifier;

      if (nextIndex < 0) {
        nextIndex = galleryPhotos.length - 1;
      } else if (nextIndex > galleryPhotos.length - 1) {
        nextIndex = 0;
      }

      this.prevDirection = vectorModifier;
      this.updatePhotoSignatureText(nextIndex);
      this.setState({ currentIndex: nextIndex });
    }
  };

  private updatePhotoSignatureText = async (photoIndex: number) => {
    const {
      photoDataRequests,
      galleryPhotos,
      content: { connector },
    } = this.props;

    let signature = "";
    const currentPhoto = galleryPhotos[photoIndex];

    if (this.loadedSignatures.has(currentPhoto.id)) {
      signature = this.loadedSignatures.get(currentPhoto.id) || "";
    } else if (currentPhoto.photographer) {
      signature = `${photoIndex + 1} ${connector} ${galleryPhotos.length} © ${
        currentPhoto.photographer
      }`;

      if (currentPhoto.description) {
        signature += ` - ${currentPhoto.description}`;
      }

      this.loadedSignatures.set(currentPhoto.id, signature);
    } else {
      const photoDataPromise = photoDataRequests.get(currentPhoto.id);

      if (photoDataPromise) {
        const photo = await photoDataPromise;

        if (photo) {
          signature = `${photoIndex + 1} ${connector} ${
            galleryPhotos.length
          } © ${photo.photographer}`;

          if (photo.description) {
            signature += ` - ${photo.description}`;
          }

          this.loadedSignatures.set(currentPhoto.id, signature);
        }
      }
    }

    this.setState({ signature });
  };

  private animateImageMovingTo = (leftPosition: number) => {
    const { current: image } = this.imageRef;

    if (image) {
      anime({
        targets: image,
        duration: ANIMATION_DURATION,
        translateX: leftPosition,
        easing: "linear",
      });
    }
  };

  private delayAction = (...callBacks: VoidFunction[]) =>
    void setTimeout(
      () => callBacks.forEach((callBack) => callBack()),
      ACTION_DURATION
    );

  private delayAnimation = (callBack: VoidFunction) =>
    void setTimeout(callBack, ANIMATION_DURATION);

  private createShadowBox = () => {
    const { current: shadowBox } = this.shadowBoxRef;
    const { current: image } = this.imageRef;

    if (image && shadowBox) {
      const { height: imgHeight, width: imgWidth } = image;

      shadowBox.style.height = imgHeight + "px";
      shadowBox.style.width = imgWidth + "px";
      image.style.position = "absolute";
    }
  };

  private unmountShadowBox = () => {
    const { current: shadowBox } = this.shadowBoxRef;
    const { current: image } = this.imageRef;

    if (image && shadowBox) {
      shadowBox.style.height = "0";
      shadowBox.style.width = "0";
      image.style.position = "relative";
    }
  };

  private hideControls = () => this.toggleControlVisibility("0");

  private showControls = () => this.toggleControlVisibility("1");

  private toggleControlVisibility = (opacity: "1" | "0") => {
    const { current: galleryContainer } = this.galleryContainerRef;

    if (galleryContainer) {
      galleryContainer
        .querySelectorAll(".control")
        .forEach((control) => ((control as any).style.opacity = opacity));
    }
  };

  private loadImageHandler = () => {
    const { current: image } = this.imageRef;
    const { current: spinner } = this.spinnerRef;
    const { current: placeholder } = this.placeholderRef;
    const { current: galleryContainer } = this.galleryContainerRef;

    if (image && spinner && placeholder && galleryContainer) {
      const { clientHeight, clientWidth } = galleryContainer;

      const { height: imageHeight, width: imageWidth } = image;
      const { height, width } = this.getImageProportionalSize(
        imageHeight,
        imageWidth,
        clientWidth,
        clientHeight
      );

      this.resizePlaceholder(spinner, placeholder, image, height, width);
    }
  };

  private resizePlaceholder = (
    spinner: HTMLDivElement,
    placeholder: HTMLDivElement,
    image: HTMLImageElement,
    height: number,
    width: number
  ) => {
    spinner.style.display = "none";

    anime({
      targets: placeholder,
      duration: ANIMATION_DURATION,
      width,
      height,
      easing: "linear",
    });

    this.delayAnimation(() => {
      placeholder.style.display = "none";
      image.style.display = "block";
    });
  };

  private resetPlaceholderStyles = () => {
    const { current: spinner } = this.spinnerRef;
    const { current: placeholder } = this.placeholderRef;

    if (spinner && placeholder) {
      placeholder.style.display = "block";
      spinner.style.display = "block";

      this.hideImage();
    }
  };

  private getImageProportionalSize = (
    imageHeight: number,
    imageWidth: number,
    maxWidth: number,
    maxHeight: number
  ) => {
    const proportionalSize = {
      height: 0,
      width: 0,
    };

    if (imageHeight > maxHeight || imageWidth > maxWidth) {
      if (imageHeight > imageWidth) {
        if (imageHeight / imageWidth > maxHeight / maxWidth) {
          proportionalSize.height = maxHeight;
          proportionalSize.width = (maxHeight / imageHeight) * imageWidth;
        } else {
          proportionalSize.height = (maxWidth / imageWidth) * imageHeight;
          proportionalSize.width = maxWidth;
        }
      } else {
        if (imageHeight / imageWidth < maxHeight / maxWidth) {
          proportionalSize.height = (maxWidth / imageWidth) * imageHeight;
          proportionalSize.width = maxWidth;
        } else {
          proportionalSize.height = maxHeight;
          proportionalSize.width = (maxHeight / imageHeight) * imageWidth;
        }
      }
    } else {
      proportionalSize.height = imageHeight;
      proportionalSize.width = imageWidth;
    }

    return proportionalSize;
  };
}
